import { useEffect, useState } from 'react';
import {
  downloadFile,
  downloadFile2,
} from '@/generated/hooks/bishop/files/v1/api';
import { toast } from '@kognitos/lattice';
import i18n from '@/shared/i18n';
import { parseContentDispositionFilename } from '@/shared/utils';
import { createConcurrencyLimiter } from '@/shared/utils/limit-concurrency';
import { parseFileResourceName } from '@/shared/utils/file-utils';
import type { IFileData } from '../types';

/**
 * Maximum number of file downloads allowed in flight at once.
 *
 * Kept low deliberately: the gateway applies a per-request timeout, so a burst
 * of large parallel downloads makes the slowest ones fail rather than making
 * all of them arrive sooner.
 */
const MAX_CONCURRENT_FILE_DOWNLOADS = 3;

export type { IFileData } from '../types';

/**
 * In-memory cache for downloaded files
 * Prevents re-downloading the same file multiple times
 * Key: file URL, Value: file data
 */
const fileCache = new Map<string, IFileData>();

/**
 * Cache for in-flight download promises
 * Prevents multiple simultaneous downloads of the same file
 * Key: file URL, Value: promise that resolves to file data
 */
const inFlightRequests = new Map<string, Promise<IFileData>>();

/**
 * Caps how many file downloads are in flight at once.
 *
 * A run log step can render many file cards simultaneously, each of which
 * previously fired its own full-blob GET on mount. That saturated the tab's
 * connection pool and pushed the slowest requests past the gateway's request
 * timeout, so files that would have loaded fine on their own failed instead.
 * Queueing them keeps every request inside the timeout window.
 *
 * Shared module-level state, matching the cache and in-flight maps above.
 */
const downloadLimit = createConcurrencyLimiter(MAX_CONCURRENT_FILE_DOWNLOADS);

/**
 * Incremented per file on eviction. A download that started before an eviction
 * finishes holding pre-replace bytes, so it compares generations before writing
 * to the cache and drops its result if it lost the race.
 * Key: file URL, Value: number of times it has been evicted
 */
const cacheGenerations = new Map<string, number>();

/**
 * Drops a file's cached bytes so the next fetch re-downloads them.
 *
 * Needed when a file's *content* changes so previews and downloads
 * would otherwise serve the previous bytes until the page reloads.
 *
 * @param fileUrl - Resource name of the file whose cached bytes are now stale
 */
export function evictCachedFile(fileUrl: string): void {
  fileCache.delete(fileUrl);
  inFlightRequests.delete(fileUrl);
  // Deleting the in-flight entry doesn't cancel the download that owns it.
  cacheGenerations.set(fileUrl, (cacheGenerations.get(fileUrl) ?? 0) + 1);
}

/**
 * The bytes already downloaded for a file, if any, without fetching.
 *
 * @param fileUrl - Resource name of the file
 * @returns The cached file data, or undefined
 */
export function getCachedFile(fileUrl: string): IFileData | undefined {
  return fileCache.get(fileUrl);
}

/**
 * Export caches for testing purposes
 * @internal
 */
export const __TEST__ = {
  fileCache,
  inFlightRequests,
  /** Clears queued/active slots so a pending download cannot leak across tests. */
  resetDownloadLimit: () => downloadLimit.reset(),
};

/**
 * Standalone function that fetches a remote file with caching and request deduplication.
 * Shares the same in-memory cache as the useFileDownload hook, so data fetched
 * by one is available to the other without duplicate network requests.
 *
 * @param fileUrl - The resource name URL of the file to fetch
 * @returns The fetched file data (name, blob, contentType)
 * @throws {Error} If the file URL is invalid or the download fails
 */
export async function fetchRemoteFile(fileUrl: string): Promise<IFileData> {
  // Check cache first - if already downloaded, use cached data
  const cachedFile = fileCache.get(fileUrl);
  if (cachedFile) {
    return cachedFile;
  }

  // Check if download is already in progress - wait for it instead of starting a new one
  const inFlightRequest = inFlightRequests.get(fileUrl);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  // Validate file URL format before starting download
  const parsed = parseFileResourceName(fileUrl);
  if (!parsed) {
    throw new Error(
      i18n.t('outputs.error.invalidFileFormat', { ns: 'outputs' }),
    );
  }

  // Snapshot taken before the request: if it no longer matches once the bytes
  // land, this download raced an eviction and its result is already stale.
  const generation = cacheGenerations.get(fileUrl) ?? 0;
  const isCurrent = () => (cacheGenerations.get(fileUrl) ?? 0) === generation;

  // Create and store the download promise. The limiter queues the actual
  // request, but the promise is created and cached synchronously below so
  // concurrent callers for the same URL still dedupe while waiting in line.
  const downloadPromise = downloadLimit(async (): Promise<IFileData> => {
    try {
      // Fetch file content for preview using generated API client
      // The blob response is handled in JavaScript and won't trigger a browser download
      const downloadOptions = {
        // XHR request - prevents browser from treating this as a navigation/download
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      };
      const response = parsed.workspace
        ? await downloadFile2(
            parsed.organization,
            parsed.workspace,
            parsed.file,
            downloadOptions,
          )
        : await downloadFile(parsed.organization, parsed.file, downloadOptions);

      // Extract metadata from headers
      const filename = parseContentDispositionFilename(
        response.headers['content-disposition'],
        parsed.file,
      );
      const contentType = response.headers['content-type'];

      const fileData: IFileData = {
        name: filename,
        blob: response.data,
        contentType,
      };

      // Cache the file data for future use, unless an eviction landed while
      // this was downloading — writing now would restore the replaced bytes.
      if (isCurrent()) fileCache.set(fileUrl, fileData);

      return fileData;
    } finally {
      // Remove from in-flight requests once complete (success or failure). After
      // an eviction this entry is already gone and any replacement belongs to a
      // newer download, so leave it be.
      if (isCurrent()) inFlightRequests.delete(fileUrl);
    }
  });

  // Store the promise so other concurrent requests can wait for it
  inFlightRequests.set(fileUrl, downloadPromise);

  return downloadPromise;
}

/**
 * Custom hook for downloading and caching remote files
 *
 * Features:
 * - Caches downloaded files to prevent duplicate network requests
 * - Deduplicates concurrent requests for the same file
 * - Manages loading and error states
 *
 * Uses fetchRemoteFile internally, sharing the same in-memory cache.
 *
 * @param fileUrl - The URL of the file to download (null if no file to download)
 * @param options - `notifyOnError: false` skips the failure toast
 * @returns File data, loading/error state, the original error cause and an in-place retry callback
 */
export function useFileDownload(
  fileUrl: string | null,
  { notifyOnError = true }: { notifyOnError?: boolean } = {},
) {
  const [fileData, setFileData] = useState<IFileData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCause, setErrorCause] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  // eslint-disable-next-line no-restricted-syntax -- reason: Fetch file data when fileUrl changes
  useEffect(() => {
    let active = true;
    setFileData(null);
    setError(null);
    setErrorCause(null);
    setIsLoading(false);
    // If no file URL provided, reset state and return
    if (!fileUrl) {
      setFileData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const doFetch = async () => {
      // Check cache first to avoid setting loading state unnecessarily
      const cachedFile = fileCache.get(fileUrl);
      if (cachedFile) {
        setFileData(cachedFile);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await fetchRemoteFile(fileUrl);
        if (active) setFileData(data);
      } catch (err) {
        if (!active) return;
        setErrorCause(err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : i18n.t('fileRenderer.error.failedToFetch', {
                ns: 'file-renderer',
              });
        setError(errorMessage);
        if (notifyOnError) {
          toast.error(
            i18n.t('outputs.toast.unableToLoadFileRetry', { ns: 'outputs' }),
          );
        }
      } finally {
        if (active) setIsLoading(false);
      }
    };

    void doFetch();
    return () => {
      active = false;
    };
  }, [fileUrl, attempt, notifyOnError]);

  return {
    fileData,
    isLoading,
    error,
    errorCause,
    retry: () => setAttempt(current => current + 1),
  };
}
